import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { useTheme } from "../../hooks";
import { toSurfaceTint } from "../../utils/colorManager";

/**
 * The student this payment is for, shown above every step after the first.
 * It only reads the journey; the way back is the owner's `onReturn`, so the
 * strip never moves a step itself.
 */
const StudentStrip = ({
  action,
  onReturn,
}: {
  // Searched journeys change the student; declared ones edit the details.
  action: "change_student" | "edit_details";
  onReturn: () => void;
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { scenario, student, firstName, lastName, matricule } = useSchoolFee();

  const enrolment = student?.enrolments[0];
  const name = student
    ? `${student.student.first_name} ${student.student.last_name}`.trim()
    : `${firstName} ${lastName}`.trim();
  const number = student?.student.matricule ?? matricule;
  // Registration carries no student number: the school issues one later.
  const detail =
    scenario === "C"
      ? t("schoolfee.strip.new_student")
      : [number, enrolment?.course.name, enrolment?.level.name]
          .filter(Boolean)
          .join(" · ");

  if (!name) return null;

  return (
    <Box
      role="region"
      aria-label={t("schoolfee.strip.label")}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
        p: 1.5,
        borderRadius: 3,
        // The dummy's strip is a panel tinted from the brand, not a grey one:
        // a variant of the tenant's own colour, so a themed portal reads as
        // one surface family rather than brand-coloured controls on greys.
        bgcolor: toSurfaceTint(theme.color_secondary),
      }}
    >
      <Box minWidth={0}>
        <Typography fontWeight={600} noWrap>
          {name}
        </Typography>
        {detail && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {detail}
          </Typography>
        )}
      </Box>
      <Button
        variant="text"
        onClick={onReturn}
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        {t(`schoolfee.strip.${action}`)}
      </Button>
    </Box>
  );
};

export default StudentStrip;
